import React, { ReactNode, useEffect } from "react";
import { PolyIcon } from "elements";
import { useHistory } from "polyrouter";
import { Form, Formik } from "collections/Form";
import { object, string, InferType } from "yup";
import { FormikValues, FormikProps } from "formik/dist/types";
//Type imports are hard, this is an exception to only importing from collections/Form
import { TypeSafeForm, NamedForm, FromForkmik } from "collections/Form/Form";
import { useTranslation } from "utilities/i18n";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

export function useModalDispatch(): () => void {
  const tenantId = useTenantId();
  const { length, goBack, replace: BrowserReplace } = useHistory();

  if (length > 1) {
    return goBack;
  }
  const homeURL = tenantId ? "/?tenantId=" + tenantId : "/";
  const replace = () => BrowserReplace(homeURL);
  return replace;
}

export interface ModalProps {
  title: string;
  closeOnDimmerClick?: boolean;
  closeIcon?: boolean;
  onClose?: () => void;
  fluid?: boolean;
  className?: string;
  contentId?: string;
}

const onSubmitPlaceholder = () => {};

type DivProps = Omit<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
  "onSubmit"
>;

type ModalFormProps<Values extends FormikValues = FormikValues> = DivProps &
  FromForkmik<Values> &
  NamedForm &
  TypeSafeForm<Values> &
  ModalProps;

type ModalConfirmProps = {
  title: string;
  message: string | ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  open: boolean;
};

/**
 * A confirm dialog that follows our modal design patter.
 * Neither routed, nor a form, its just a confirm/cancel.
 * @param title
 * @param onConfirm
 * @param onCancel
 * @param open
 * @param message
 * @constructor
 */
export function ModalConfirm({
  title,
  onConfirm,
  onCancel,
  open,
  message,
  confirmLabel
}: ModalConfirmProps): JSX.Element {
  const { t } = useTranslation();
  if (!open) {
    return <></>;
  }
  return (
    <div className="modal">
      <div className="overlay" onClick={() => {}} />
      <div className="modal-content form grid fixed" id="confirm-modal">
        <h4>{title}</h4>
        <div className="contents-container">
          <div data-testid="confirm-modal">
            <div className="small-text" data-testid="confirmation">
              {message}
            </div>
            <div className="btn-container">
              <button
                type="button"
                className="btn"
                data-testid="cancel"
                onClick={onCancel}
              >
                {t("CANCEL")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-testid="confirm"
                onClick={onConfirm}
              >
                {confirmLabel ?? t("CONFIRM")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

//Modals that are actually forms should extend this directly
export function ModalForm<Values extends FormikValues = FormikValues>({
  title,
  closeOnDimmerClick,
  closeIcon = false,
  onClose,
  fluid,
  className,
  contentId,
  children,
  onSubmit,
  initialValues,
  name,
  validationSchema,
  customDimensions,
  ...rest
}: ModalFormProps<Values>): JSX.Element {
  const fluidStyle = fluid ? "fluid" : "fixed";
  useEffect(() => {
    document.body.classList.toggle("no-scroll", true);
    return () => {
      document.body.classList.toggle("no-scroll", false);
    };
  });
  const dispatchEvent = useModalDispatch();
  const addedClasses = className ?? "";

  if (!onClose) onClose = dispatchEvent;
  return (
    <div className="modal">
      {closeOnDimmerClick === false ? (
        <div className="overlay blocking" />
      ) : (
        <div className="overlay" onClick={onClose} />
      )}
      <div
        className={`modal-content form grid ${fluidStyle} ${addedClasses}`}
        id={contentId}
      >
        {closeIcon && (
          <span className="clear-icon" data-testid="clear" onClick={onClose}>
            <PolyIcon title="close" icon="clear_x" />
          </span>
        )}

        {title && <h4>{title}</h4>}
        <div className="contents-container">
          <Formik<Values>
            name={name}
            initialValues={initialValues}
            onSubmit={onSubmit || onSubmitPlaceholder}
            validationSchema={validationSchema}
            children={children}
            {...rest}
          />
        </div>
      </div>
    </div>
  );
}

const defaultValidationSchema = object({
  val: string()
});

type DefaultModalFormType = InferType<typeof defaultValidationSchema>;

const initialState: DefaultModalFormType = {
  val: ""
};

type DefaultModalFormProps = Pick<
  ModalFormProps<DefaultModalFormType>,
  | "title"
  | "onSubmit"
  | "className"
  | "children"
  | "name"
  | "closeIcon"
  | "closeOnDimmerClick"
  | "fluid"
> & {
  validationSchema?: any;
};

//Modals that only display message (not forms) should extend this
export function DefaultModalForm({
  title,
  onSubmit,
  className,
  children,
  name,
  closeIcon,
  closeOnDimmerClick,
  fluid,
  validationSchema
}: DefaultModalFormProps): JSX.Element {
  const dispatchEvent = useModalDispatch();
  let wrapChildren:
    | ((props: FormikProps<DefaultModalFormType>) => React.ReactNode)
    | React.ReactNode = <Form>{children}</Form>;
  if (typeof children === "function") {
    const childFun = children as (
      props: FormikProps<DefaultModalFormType>
    ) => React.ReactNode;
    wrapChildren = (props: FormikProps<DefaultModalFormType>) => (
      <Form>{childFun(props)}</Form>
    );
  }
  return (
    <ModalForm
      title={title}
      onClose={dispatchEvent}
      onSubmit={onSubmit}
      className={className || ""}
      name={name}
      initialValues={initialState as any}
      validationSchema={
        validationSchema ? validationSchema : defaultValidationSchema
      }
      closeIcon={closeIcon}
      closeOnDimmerClick={closeOnDimmerClick}
      fluid={fluid}
      children={wrapChildren}
    />
  );
}
